import { fetchDestinationImageId } from 'actions/PlaceActions'
import { useAppNavigation } from 'lib/routing/useAppNavigation'
import { getListName } from 'analytics/enhanced-ecommerce'
import type { OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList from 'components/OfferList/OfferList'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import type { GlobalSearchAction } from 'contexts/GlobalSearch/GlobalSearchState'
import { GLOBAL_SEARCH_INITIAL_STATE, GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import { usePlaceById } from 'hooks/usePlace'
import getOfferListKey from 'lib/offer/offerListKey'
import { buildSelectDatesSearchParams } from 'lib/search/selectDatesUtils'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useMatch } from 'react-router-dom'
import { trackClientEvent } from 'analytics/analytics'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import type { SearchFormRef } from 'components/Search/SearchForm'
import SearchForm from 'components/Search/SearchForm'
import { DATE_SEARCH_OPTION_IDS, HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import LayoutContainer from 'components/Common/LayoutContainer'
import { getImageUrl } from 'lib/image/imageUtils'
import {
  buildSearchParamsForFilterPane,
  decodeFromUrlString,
  DestinationLandingPageType,
  getDestinationLandingPageType,
  getFilters,
  getTitleAndSubTitle,
  validateHolidayType,
  isMetadataExperimentOn,
  getMetaDescription,
  getMetaTitle,
} from './DestinationLandingPageUtils'
import HeadData from 'components/Common/HeadData'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import type { FilterControlChangeHandler } from '../HotelSearchPage/HotelSearchPageFilterPane'
import HotelSearchPageFilterPane from '../HotelSearchPage/HotelSearchPageFilterPane'
import { deleteSearchParams, parseSearchString } from 'lib/url/searchUrlUtils'
import config from 'constants/config'
import type { EnabledFilters } from 'components/Search/type'
import clsx from 'clsx'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import useOfferList from 'hooks/Offers/useOfferList'
import { getDestinationImage, getSearchKey, getSearchTypeFromFilters } from 'lib/search/searchUtils'
import HotelSearchHeader from '../HotelSearchPage/HotelSearchHeader'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import OfferCountAndSort from '../HotelSearchPage/OfferCountAndSort'

import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import { setSearchEvent } from 'actions/SearchActions'
import { pushWithRegion } from 'actions/NavigationActions'

const Banner = styled.div<{ imageUrl?: string }>`
  width: 100vw;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: ${rem(48)};

  & > h1, & > h2 {
    margin: 0 auto;
    padding: 0;
  }

  ${mediaQueryUp.tablet} {
    margin-bottom: ${rem(16)};
  }

  background: linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%), url(${props => props.imageUrl}) lightgray 50% / cover no-repeat;
`
const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
`
const StyledHotelSearchPageFilterPane = styled(HotelSearchPageFilterPane)`
  grid-area: offer-filters;
`

const SearchFormContainer = styled.div`
${mediaQueryUp.tablet} {
  padding-block: 0 ${rem(16)};
  padding-inline: ${rem(10)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
}
`

interface MatchProps {
  holidayType?: string
  location?: string
  regionCode?: string
}

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    display: grid;
    padding: ${rem(24)} 0;
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 2fr);
      padding-top: ${rem(12)};
    }
  }

  &.fullWidth {
    padding: ${rem(16)} 0;
  }
`

interface Props {
  filters: App.OfferListFilters
  regionCode: string
  isSearchEnabled: boolean
  locationToPlace: { [location: string]: App.Place }
  placesLoading: { [location: string]: boolean }
  customHeadings?: App.ContentHeadings
  windowSearch: string
  filtersFromUrl: App.OfferListFilters
  placesLookUp: { [id: string]: App.Place }
}

function DestinationLandingPage(props: Props) {
  const { locationToPlace, windowSearch, filters, placesLookUp } = props
  const intl = useIntl()
  const { searchSortByTypes } = useOfferListSortOptions()
  const sitelinks = useAppSelector(state => state.marketing.sitelinks)
  const formRef = useRef<SearchFormRef>(null)
  const dispatch = useAppDispatch()
  const holidayTypeMatch = useMatch<'regionCode' | 'location' | 'holidayType', string>('/:regionCode/holiday-packages/:location/:holidayType')
  const holidayPackagesMatch = useMatch<'regionCode' | 'location', string>('/:regionCode/holiday-packages/:location')
  const resortsMatch = useMatch<'regionCode' | 'location', string>('/:regionCode/resorts/:location')
  const match = holidayTypeMatch ?? holidayPackagesMatch ?? resortsMatch
  const url = match?.pathname ?? ''
  const routePath = match?.pattern.path ?? ''
  const params: MatchProps = match?.params ?? {}
  const queryParams = useQueryParams()
  const isDesktop = useScreenSizeUp('largeDesktop')
  const { holidayType: urlHolidayType, location: urlLocation } = params
  const holidayType = urlHolidayType ? decodeFromUrlString(urlHolidayType) : ''
  const hasHolidayType = validateHolidayType(holidayType) && holidayType !== ''
  const location = decodeFromUrlString(urlLocation ?? '')
  const isMobile = useScreenSizeOnly('mobile')
  const destinationLandingPageType = getDestinationLandingPageType(routePath)
  const place = locationToPlace[location]
  // Slug lookup gives the placeId; Place API provides the localized display.
  const localizedPlace = usePlaceById(place?.id ?? '')
  const displayName = localizedPlace?.name ?? place?.name ?? ''
  const {
    title, subtitle,
  } = getTitleAndSubTitle({ destinationLandingPageType, placeName: displayName, holidayType, intl })
  const navigation = useAppNavigation()
  const searchParamsForFilterPane = buildSearchParamsForFilterPane({
    originalSearch: windowSearch,
    holidayType,
    destinationName: place?.name ?? '',
    destinationId: place?.id ?? '',
  })

  const isExperimentOn = isMetadataExperimentOn(url, destinationLandingPageType)

  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(searchParamsForFilterPane, 'sortBy')}`, [searchParamsForFilterPane])
  const showFilterPane = isDesktop
  const placeId = place?.id
  const imageId = (placeId ? getDestinationImage(placeId) : undefined) || config.searchFormSection.hotelsImageId
  const imageUrl = getImageUrl(imageId)

  const globalHotelsSearchDispatchRef = useRef<React.Dispatch<GlobalSearchAction> | null>(null)

  const onAnyHotelSelectDatesPrompt = useCallback((offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary) => {
    const { propertySearchItem } = buildSelectDatesSearchParams(offer)

    globalHotelsSearchDispatchRef.current?.({
      type: GlobalSearchStateActions.SET_SEARCH_ITEM,
      searchItem: propertySearchItem,
    })
    globalHotelsSearchDispatchRef.current?.({ type: GlobalSearchStateActions.SET_DATE_SEARCH_OPTION, optionId: DATE_SEARCH_OPTION_IDS.SPECIFIC })

    // formRef is shared with HotelSearchHeader's mobile SearchForm below, so this opens
    // whichever instance is actually mounted at the current breakpoint.
    formRef.current?.form.current?.scrollIntoView()
    formRef.current?.openDatePickerMenu()
  }, [])

  // Deliberately not wiring hideAnytimePrices here: this page never hid anytime prices before
  // force-dates existed, and that's not part of this feature's scope. The force-dates click
  // gate on OfferSearchTile/GroupedBedbankSearchTile derives its own anytime-search signal
  // from the `filters` prop it already receives — price visibility on this page is unchanged.
  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType: 'destination',
    searchItem: { searchType: 'destination', value: place?.id, format: { mainText: displayName, secondaryText: '' } },
    searchTargetLabel: displayName,
    onAnyHotelSelectDatesPrompt,
  }, queryParams)

  useEffect(() => {
    globalHotelsSearchDispatchRef.current = globalHotelsSearchDispatch
  }, [globalHotelsSearchDispatch])

  const listFilters = useMemo((): App.OfferListFilters => {
    return getFilters(
      {
        filters,
        destinationLandingPageType,
        hasHolidayType,
        urlHolidayType: urlHolidayType ?? '',
        place,
      })
  }, [destinationLandingPageType, hasHolidayType, place, urlHolidayType, filters])

  useEffect(() => {
    // Replace the search params in the url use react router
    // this will allow the filter pane to work
    navigation.replace({
      search: searchParamsForFilterPane,
    })
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    dispatch(fetchDestinationImageId(place?.id))
  }, [place, dispatch, location, sitelinks, locationToPlace])

  const hotelOfferList = useOfferList(listFilters)
  const mainListTotal = hotelOfferList.offerCount

  const enabledFilters: EnabledFilters = useMemo(() => (
    {
      inclusions: true,
      amenities: true,
      customerRating: true,
      priceFilter: config.ENABLE_HOTEL_SEARCH_PAGE_PRICE_FILTER,
      holidayTypes: true,
      propertyTypes: true,
      locations: true,
      popularFilters: true,
      luxPlusFeatures: true,
      agentHubFeatures: config.agentHub.isEnabled,
      loyalty: false,
    }
  ), [])

  const trackingName = destinationLandingPageType === DestinationLandingPageType.HolidayPackages ? 'HolidayPackage Page' : 'Resort Page'
  const tracking = useOfferListTracking(
    getListName('OfferList', trackingName, 'Main Results'),
    {
      ga: {
        category: 'OfferList',
        label: 'Main Results',
      },
      key: getOfferListKey(listFilters),
    },
  )

  const searchType = getSearchTypeFromFilters(listFilters)
  const offerListFiltersProps = {
    searchSortByTypes,
    searchType,
    switchView: {
      label: intl.formatMessage({
        id: '_NPZ5LK-tc5xPuMzHyxg',
        defaultMessage: 'Map view',
        description: 'Label for map view switch button on destination landing page',
      }),
      view: 'map' as const,
      URL: mapViewURL,
    },
    listFilters,
  }

  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    if (dispatchAction.type === OfferListEvents.productClick) {
      trackClientEvent({
        subject: 'search-click-position',
        action: `ab-value: ${dispatchAction.position + 1}`,
        category: 'logging',
        type: 'operational',
        optimizelyEventId: '29214570602',
        optimizelyEventKey: 'search-click-position',
      })
    }
  }, [])

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(setSearchEvent(
      getSearchKey(parseSearchString(params.search)),
      { searchEvent: { type: 'filters' } },
    ))
    dispatch(pushWithRegion('/search', params.search, undefined, params.hash))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(setSearchEvent(
      getSearchKey(parseSearchString(params.search)),
      { searchEvent: { type: 'sort' } },
    ))
    dispatch(pushWithRegion('/search', params.search))
  }, [dispatch])

  const specificPlaces = useMemo<Array<App.SpecificPlace>>(() => {
    if (!sitelinks) {
      return []
    }
    const currentPlaceName = place?.name.toLowerCase()
    const specificPlaces: Array<App.SpecificPlace> = []
    const siteLinkPlaces = sitelinks[currentPlaceName]?.data?.siteLinkPlaces ?? []

    for (const sitelinkPlaceName of siteLinkPlaces) {
      const _place = locationToPlace[sitelinkPlaceName]
      const _imageId = placesLookUp[_place?.id]?.imageId
      if (_place) {
        const name = _place?.name
        const caption = sitelinks[sitelinkPlaceName]?.data?.caption ?? ''
        const image = _imageId ?? ''
        const placeId = _place?.id

        if (!name || !caption || !image || !placeId) {
          continue
        }

        specificPlaces.push({
          name,
          caption,
          image,
          placeId,
          destinationLandingPageSlug: sitelinkPlaceName,
        })
      }
    }
    return specificPlaces
  }, [place?.name, sitelinks, locationToPlace, placesLookUp])

  if (mainListTotal === 0) {
    const regionCode = params.regionCode ?? props.regionCode
    if (holidayTypeMatch) {
      return <RedirectWithStatus code={302} to={`/${regionCode}/holiday-packages/${params.location}`} withSearchAndHash={false} />
    }
    else {
      return <RedirectWithStatus code={302} to={`/${regionCode}`} withSearchAndHash={false} />
    }
  }

  const extras: Array<OfferListV2Item> = [
    {
      id: 'heading',
      position: 'start',
      element: <OfferCountAndSort
        mainListTotal={mainListTotal as number}
        isFlexible
        filters={listFilters}
        windowSearch={searchParamsForFilterPane}
        shouldShowFilters={!showFilterPane}
        showHolidayTypes
        countTextOverride={intl.formatMessage({
          id: 'EvtEJ-IZdGemxMZUiDfR',
          defaultMessage: 'Showing {count, plural, one {# offer} other {# offers}}',
          description: 'Offer count text on destination landing page showing total number of offers',
        }, { count: mainListTotal as number })}
        sortOptions={searchSortByTypes}
        showOfferCount={destinationLandingPageType === DestinationLandingPageType.HolidayPackages}
        specificPlaces={specificPlaces}
        isDestinationLandingPage
        destinationName={place?.name}
        urlLocationForDestinationLandingPage={urlLocation}
        pageTypeForDestinationLandingPage={destinationLandingPageType}
        holidayTypeForDestinationLandingPage={holidayType}
        onFilterChange={handleFilterChange}
        onSortChange={handleSortChange}
      />,
    },
  ]
  const metaDescription = getMetaDescription({ destinationLandingPageType, place, holidayType, isExperimentOn, intl })
  const metaTitle = getMetaTitle({ destinationLandingPageType, place, holidayType, isExperimentOn, intl })

  return (
    <GlobalSearchContextProvider
      state={globalHotelsSearchState}
      dispatch={globalHotelsSearchDispatch}
    >
      <HeadData
        imageId={imageId}
        vertical="hotels"
        title={metaTitle}
        description={metaDescription}
      />

      <CSSBreakpoint max="mobile">
        <HotelSearchHeader
          filters={listFilters}
          formRef={formRef}
          offerListFiltersProps={offerListFiltersProps}
          searchVertical="hotels"
          baseSearchPath="/search"
          baseSearchMapPath="/search/map"
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
        />
      </CSSBreakpoint>
      <Banner imageUrl={imageUrl}>
        <Heading variant="heading2" align="center" as="h1" colour="dark-contrast">{title}</Heading>
        <BodyText variant="large" align="center" as="h2" colour="dark-contrast">{subtitle}</BodyText>
      </Banner>
      <CSSBreakpoint min="tablet">
        <SearchFormContainer>
          <SearchForm
            submitOnApply
            filters={listFilters}
            ref={formRef}
            showDateFieldOnMobile={false}
            typeaheadTypes={HOTEL_SEARCH_TYPEAHEAD_TYPES}
          />
        </SearchFormContainer>
      </CSSBreakpoint>
      <StyledLayoutContainer
        size={showFilterPane ? 'xl' : 'lg'}
        className={clsx({ filterPane: showFilterPane, fullWidth: isMobile })}
      >
        <StyledVerticalSpacer gap={16}>
          <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
            <OfferList
              filters={listFilters}
              tileStyle="search"
              interstitials={extras}
              fullWidth={isMobile}
            />
          </OfferListEventsProvider>
        </StyledVerticalSpacer>
        {showFilterPane &&
          <StyledHotelSearchPageFilterPane
            filters={listFilters}
            mapViewURL={mapViewURL}
            search={searchParamsForFilterPane}
            enabledFilters={enabledFilters}
            onFilterChange={handleFilterChange}
          />}
      </StyledLayoutContainer>
    </GlobalSearchContextProvider>
  )
}

const mapStateToProps = (state: App.State) => ({
  placesLookUp: state.destination.places,
  locationToPlace: state.destination.locationToPlace,
  placesLoading: state.destination.placesLoading,
  regionCode: state.geo.currentRegionCode,
  customHeadings: state.content?.secureHeadingParams?.landing,
  windowSearch: state.router.location.search,
  filters: getUrlOfferListFilters(state),
})

export default connect(mapStateToProps)(DestinationLandingPage)
